import type {MediaTrack, QualityEvent, TypedSource, VideoQuality} from 'theoplayer';

import {THEOplayerAnalyticsStateMachine} from '../../analyticsStateMachines/THEOplayerAnalyticsStateMachine';
import type {Analytics} from '../../core/Analytics';
import {DownloadSpeedMeter} from '../../core/DownloadSpeedMeter';
import {HeartbeatService} from '../../core/HeartbeatService';
import {CastTech} from '../../enums/CastTech';
import {Event, EventMap} from '../../enums/Event';
import {Player as PlayerEnum} from '../../enums/Player';
import {PlayerSize} from '../../enums/PlayerSize';
import {getStreamTypeFromMIMEType, getStreamTypesFromURL, StreamTypes} from '../../enums/StreamTypes';
import {ErrorDetailBackend} from '../../features/errordetails/ErrorDetailBackend';
import {ErrorDetailTracking} from '../../features/errordetails/ErrorDetailTracking';
import type {Feature} from '../../features/Feature';
import type {FeatureConfig} from '../../features/FeatureConfig';
import {HttpRequestTracking} from '../../features/httprequesttracking/HttpRequestTracking';
import {HttpRequestType} from '../../features/httprequesttracking/HttpRequestType';
import type {OnDownloadFinishedEventObject} from '../../features/httprequesttracking/OnDownloadFinishedEventObject';
import {AdSample} from '../../types/AdSample';
import type {DownloadSpeedInfo} from '../../types/DownloadSpeedInfo';
import type {DrmPerformanceInfo} from '../../types/DrmPerformanceInfo';
import type {QualityChangeEvent} from '../../types/EventData';
import type {FeatureConfigContainer} from '../../types/FeatureConfigContainer';
import type {NoExtraProperties} from '../../types/NoExtraProperties';
import {buildPlaybackInfoStreamTypeFields, normalizeVideoDuration, PlaybackInfo} from '../../types/PlaybackInfo';
import type {SpeedMeasurement} from '../../types/SpeedMeasurement';
import {getGlobalTheoPlayerVersion} from '../../utils/globalScopeUtils';
import {logger} from '../../utils/Logger';
import {isNumber} from '../../utils/numberUtils';
import {isBlank, isValidString} from '../../utils/stringUtils';
import {isNotNullish, isNullish, tryJSONStringify} from '../../utils/Utils';
import type {AdModuleAPI} from '../internal/ads/AdModuleAPI';
import {InternalAdapter} from '../internal/InternalAdapter';
import type {InternalAdapterAPI} from '../internal/InternalAdapterAPI';

import {isSsaiIntegration} from './ads/AdUtils';
import {THEOplayerAdModule} from './ads/THEOplayerAdModule';
import {DrmPerformanceService} from './DrmPerformanceService';
import {DelayedEventDispatcher} from './playback/DelayedEventDispatcher';
import {PlaybackProgressDetectionService} from './PlaybackProgressDetectionService';
import {THEOplayerNetworkInterceptor} from './THEOplayerNetworkInterceptor';
import {THEOplayerProxy} from './THEOplayerProxy';

export class THEOplayerInternalAdapter extends InternalAdapter implements InternalAdapterAPI {
  private readonly player: THEOplayerProxy;
  private readonly networkInterceptor: THEOplayerNetworkInterceptor;
  private readonly _adModule: THEOplayerAdModule;

  private readonly speedMeter: DownloadSpeedMeter = new DownloadSpeedMeter();
  private readonly delayedEventDispatcher: DelayedEventDispatcher = DelayedEventDispatcher.create();
  private readonly drmPerformanceService: DrmPerformanceService = new DrmPerformanceService();
  private readonly playbackProgressDetectionService: PlaybackProgressDetectionService =
    new PlaybackProgressDetectionService();

  /**
   * This flag ensures whether the "unload path" has already run once.
   *
   * The "unload path" is wired to three listeners (`destroy`, `beforeunload`, `unload`) that can all fire during teardown.
   */
  private hasHandledUnload = false;

  private lastTrackedCurrentTime: number | undefined;

  private currentSource: TypedSource | undefined;

  private videoTrackListeners: Map<MediaTrack, (event: QualityEvent<'activequalitychanged'>) => void> = new Map();
  private audioTrackListeners: Map<MediaTrack, (event: QualityEvent<'activequalitychanged'>) => void> = new Map();

  constructor(player: THEOplayerProxy) {
    super();
    this.player = player;
    this.networkInterceptor = new THEOplayerNetworkInterceptor(player);
    this._adModule = new THEOplayerAdModule(player, this.ssaiService);
    this.networkInterceptor.subscribe((event) => {
      // SpeedMeter
      const speedMeasurement = createSpeedMeasurementFromEvent(event);
      if (isNotNullish(speedMeasurement)) {
        this.speedMeter.addMeasurement(speedMeasurement);
      }

      // DrmPerformanceService
      this.drmPerformanceService.onDownloadFinished(event);
    });

    const playingHeartbeatService = new HeartbeatService(() => this.currentTime);
    this.stateMachine = new THEOplayerAnalyticsStateMachine(this.stateMachineCallbacks, playingHeartbeatService);
    playingHeartbeatService.setListener(this.stateMachine);
  }

  get adModule(): AdModuleAPI | undefined {
    return this._adModule;
  }

  initialize(analytics: Analytics): Array<Feature<FeatureConfigContainer, FeatureConfig>> {
    this.registerEventListeners();
    // We need to register AdModule player event listeners AFTER the internal adapter, because they have to be executed
    // AFTER we have set the up-to-date player information on the current Sample that is later used to form AdSample.
    this._adModule.registerEventListeners();

    this.registerUnloadEventListeners();

    this.ssaiService.activate({
      getPlayerCurrentTime: () => this.currentTime,
      getCurrentSample: () => analytics.sample,
      reportPlaybackInteraction: (timestamp: number, playerCurrentTime: number) => {
        // Because ssai playback interaction can happen before Ssai Service is active (e.g. first ssai startAd call),
        // we need to manually mark sample as triggered by ssai
        //
        // This is also including the last sample marking, before a ssai ad block (due to ssai being spiky by design)
        this.stateMachine.sendPlayingSample(timestamp, playerCurrentTime, true);
      },
      sendErrorAdSample: (adSample: AdSample, originalError?: any) => {
        analytics.sendErrorSsaiAdSample(adSample, originalError);
      },
      sendQuartileAdSample: (adSample: AdSample) => {
        analytics.sendQuartileSsaiAdSample(adSample);
      },
      sendUnloadAdSample: (adSample: AdSample) => {
        analytics.sendUnloadSsaiAdSample(adSample);
      },
    });

    const httpRequestTracking = new HttpRequestTracking([this.networkInterceptor]);
    const errorDetailTracking = new ErrorDetailTracking(
      analytics.errorDetailTrackingSettingsProvider,
      new ErrorDetailBackend(analytics.errorDetailTrackingSettingsProvider.collectorConfig),
      [analytics.errorDetailSubscribable],
      httpRequestTracking,
    );

    this.eventCallback(Event.READY, {currentTime: this.currentTime});

    return [errorDetailTracking];
  }

  getPlayerVersion(): string {
    // try to get the version from the player instance
    // Note: THEOplayer instance `version` was introduced in 11.1.0, so it might not be available in older versions
    // https://optiview.dolby.com/docs/theoplayer/changelog/#-1110-20260428
    const playerInstanceVersion = this.player.version;
    if (isValidString(playerInstanceVersion) && !isBlank(playerInstanceVersion)) {
      return playerInstanceVersion;
    }

    // try to get the version from the global `window.THEOplayer`
    const windowTHEOplayerVersion = getGlobalTheoPlayerVersion();
    if (isValidString(windowTHEOplayerVersion) && !isBlank(windowTHEOplayerVersion)) {
      return windowTHEOplayerVersion;
    }

    return 'unknown';
  }
  getPlayerName(): string {
    return PlayerEnum.THEOPLAYER;
  }
  getPlayerTech(): string {
    return 'html5';
  }
  getAutoPlay(): boolean {
    return this.player.autoplay ?? false;
  }

  override getDrmPerformanceInfo = (): DrmPerformanceInfo | undefined => this.drmPerformanceService.getInfo();

  getCurrentPlaybackInfo(): PlaybackInfo {
    const activeVideoTrack = this.player.videoTracks?.find((t) => t.enabled);
    const activeAudioTrack = this.player.audioTracks?.find((t) => t.enabled);
    const activeTextTrack = this.player.textTracks?.find((t) => t.mode === 'showing');

    let castTech: CastTech | undefined = undefined;
    if (isNotNullish(this.player.cast?.airplay?.casting)) {
      castTech = CastTech.AirPlay;
    } else if (isNotNullish(this.player.cast?.chromecast?.casting)) {
      castTech = CastTech.GoogleCast;
    }

    const playerDuration = this.player.duration;

    return {
      ...this.getCommonPlaybackInfo(),
      ...detectPlaybackInfoStreamTypeFieldsFromSource(this.currentSource),
      audioBitrate: activeAudioTrack?.activeQuality?.bandwidth,
      audioCodec: activeAudioTrack?.activeQuality?.codecs,
      audioLanguage: isBlank(activeAudioTrack?.language) ? undefined : activeAudioTrack?.language,
      castTech: castTech,
      droppedFrames: 0,
      isCasting: isNotNullish(castTech),
      isLive: !isNullish(playerDuration) && !isFinite(playerDuration),
      isMuted: this.player.muted,
      playerTech: this.getPlayerTech(),
      size: this.player.presentation?.currentMode === 'fullscreen' ? PlayerSize.Fullscreen : PlayerSize.Window,
      subtitleEnabled: isNotNullish(activeTextTrack),
      subtitleLanguage: isBlank(activeTextTrack?.language) ? undefined : activeTextTrack?.language,
      videoBitrate: activeVideoTrack?.activeQuality?.bandwidth,
      videoCodec: activeVideoTrack?.activeQuality?.codecs,
      videoDuration: normalizeVideoDuration(playerDuration),
      videoPlaybackHeight: this.player.videoHeight,
      videoPlaybackWidth: this.player.videoWidth,
      videoWindowHeight: this.player.element?.offsetHeight,
      videoWindowWidth: this.player.element?.offsetWidth,
    } as PlaybackInfo;
  }

  protected override get currentTime(): number {
    return this.player.currentTime ?? 0;
  }

  override getDownloadSpeedInfo(): DownloadSpeedInfo {
    return this.speedMeter.getInfo();
  }

  override resetDownloadSpeedInfoMeasures(): void {
    this.speedMeter.reset();
  }

  override resetSourceRelatedState(): void {
    super.resetSourceRelatedState();
    this.ssaiService.reset();

    this.videoTrackListeners.forEach((listener, track) => {
      track.removeEventListener('activequalitychanged', listener);
    });
    this.videoTrackListeners.clear();

    this.audioTrackListeners.forEach((listener, track) => {
      track.removeEventListener('activequalitychanged', listener);
    });
    this.audioTrackListeners.clear();

    this.lastTrackedCurrentTime = undefined;
    this.currentSource = undefined;
    this.speedMeter.reset();
    this.drmPerformanceService.reset();
    this.networkInterceptor.reset();
    this.playbackProgressDetectionService.reset();
  }

  override eventCallback<
    StateMachineEvent extends keyof EventMap,
    StateMachineEventData extends EventMap[StateMachineEvent],
  >(event: StateMachineEvent, eventData: NoExtraProperties<EventMap[StateMachineEvent], StateMachineEventData>) {
    this.delayedEventDispatcher.dispatchEvent(
      event,
      eventData,
      this.stateMachine.currentState,
      super.eventCallback.bind(this),
    );
  }

  private handleQualityChanged(qualityEvent: QualityEvent<'activequalitychanged'>, type: 'audio' | 'video'): void {
    const quality = qualityEvent.quality;
    const currentVideoPosition = this.currentTime;

    let videoQualityEventInfo: QualityChangeEvent;
    let audioQualityEventInfo: QualityChangeEvent;

    if (type === 'video') {
      // The issue is that the quality from the activequalitychanged event has a generic type Quality, which only has
      // the base properties. But the actual object has more properties for video tracks, so we need to narrow the type
      // to VideoQuality to access them.
      const videoQuality = quality as VideoQuality;
      videoQualityEventInfo = {
        type: 'video',
        width: videoQuality.width,
        height: videoQuality.height,
        bitrate: videoQuality.bandwidth,
        currentTime: currentVideoPosition,
      };

      const activeAudioTrack = this.player.audioTracks?.find((t) => t.enabled);
      audioQualityEventInfo = {
        type: 'audio',
        bitrate: activeAudioTrack?.activeQuality?.bandwidth,
        currentTime: currentVideoPosition,
      };
    } else {
      audioQualityEventInfo = {
        type: 'audio',
        bitrate: quality.bandwidth,
        currentTime: currentVideoPosition,
      };
      const activeVideoTrack = this.player.videoTracks?.find((t) => t.enabled);
      // The issue is that the video track is typed as the base MediaTrack interface with the base Quality type for
      // activeQuality, so we need to narrow the type to VideoQuality
      const activeVideoQuality = activeVideoTrack?.activeQuality as VideoQuality | undefined;
      videoQualityEventInfo = {
        type: 'video',
        width: activeVideoQuality?.width,
        height: activeVideoQuality?.height,
        bitrate: activeVideoQuality?.bandwidth,
        currentTime: currentVideoPosition,
      };
    }

    if (type === 'audio' && this.qualityChangeService.shouldAllowAudioQualityChange(quality.bandwidth)) {
      this.eventCallback(Event.AUDIO_CHANGE, audioQualityEventInfo);
    } else if (type === 'video' && this.qualityChangeService.shouldAllowVideoQualityChange(quality.bandwidth)) {
      this.eventCallback(Event.VIDEO_CHANGE, videoQualityEventInfo);
    }

    const videoBitrate = videoQualityEventInfo.bitrate;
    if (isNotNullish(videoBitrate)) {
      this.qualityChangeService.setVideoBitrate(videoBitrate);
      this.stateMachineCallbacks.updateSampleQualityInfo(videoQualityEventInfo);
    }
    const audioBitrate = audioQualityEventInfo.bitrate;
    if (isNotNullish(audioBitrate)) {
      this.qualityChangeService.setAudioBitrate(audioBitrate);
      this.stateMachineCallbacks.updateSampleQualityInfo(audioQualityEventInfo);
    }
  }

  private registerEventListeners() {
    this.player.addEventListener('sourcechange', (event) => {
      // The 'sourcechange' event fires in two cases, which we have to disambiguate
      // via the `source` property on the event:
      //
      // 1. `source` is NOT set → a source unload happened (e.g. via `player.stop()`).
      //    No new source is being loaded, so we stay in State.SOURCE_CHANGING until another
      //    sourcechange or play event arrives.
      //
      // 2. `source` IS set → an actual source change was initiated. We use this event to
      //    signal that the player should leave State.SOURCE_CHANGING and move to State.READY.
      //    "Ready" here only means the player accepts a "play" action (play button enabled
      //    or API call available) — it does NOT mean the source is fully loaded. The actual
      //    source loading happens later as part of the startup phase.
      //
      // Why not use the 'loadedmetadata' event?
      //  Because we need to support player setups without preload (preload="none"), where
      //  'loadedmetadata' is not fired yet, but the player is already able to trigger a "play"
      //  action (play button or API call is available). The player should leave State.SOURCE_CHANGING
      //  before that — otherwise a "play" action would not transition us to State.STARTUP. Without
      //  preload, 'loadedmetadata' fires later and the whole source loading time gets tracked as
      //  part of startup.
      if (isNotNullish(event.source)) {
        this.eventCallback(Event.READY, {});
      } else {
        this.eventCallback(Event.MANUAL_SOURCE_CHANGE, {currentTime: this.lastTrackedCurrentTime, config: {}});
      }
    });

    this.player.addEventListener('currentsourcechange', (event) => {
      // prevents MANUAL_SOURCE_CHANGE on the first occurrence - in the beginning the player is setting up the first video
      if (isNotNullish(this.currentSource)) {
        // Why not use the 'loadstart' event?
        //  Because with THEOplayer, the load of the video can actually happen after startup (especially without preload: on)
        //  and using 'loadstart' to indicate the start of the source change would send startup failure instead of normal startup.
        this.eventCallback(Event.MANUAL_SOURCE_CHANGE, {currentTime: this.lastTrackedCurrentTime, config: {}});
      }
      this.currentSource = event.currentSource;
    });

    this.player.addEventListener('play', (event) => {
      this.eventCallback(Event.PLAY, {currentTime: event.currentTime});
      if (!this.stateMachine.hasStartupSucceeded) {
        this.adModule?.adCallbacks.onPlayBeforeStartupHasSucceeded();
      }
    });

    this.player.addEventListener('pause', (event) => {
      this.eventCallback(Event.PAUSE, {currentTime: event.currentTime});
    });

    this.player.videoTracks?.addEventListener('addtrack', (event) => {
      // The issue is that the track from the addtrack event is typed as the base Track interface,
      // which only has TrackEventMap — not MediaTrackEventMap. We need to narrow the type to MediaTrack
      const mediaTrack = event.track as MediaTrack;

      const listener = (qualityEvent: QualityEvent<'activequalitychanged'>) => {
        this.handleQualityChanged(qualityEvent, 'video');
      };
      mediaTrack.addEventListener('activequalitychanged', listener);
      this.videoTrackListeners.set(mediaTrack, listener);
    });

    this.player.videoTracks?.addEventListener('removetrack', (event) => {
      // The issue is that the track from the removetrack event is typed as the base Track interface,
      // which only has TrackEventMap — not MediaTrackEventMap. We need to narrow the type to MediaTrack
      const mediaTrack = event.track as MediaTrack;

      const listener = this.videoTrackListeners.get(mediaTrack);
      if (listener) {
        mediaTrack.removeEventListener('activequalitychanged', listener);
        this.videoTrackListeners.delete(mediaTrack);
      }
    });

    // add listeners to all already added/existing video tracks
    for (const track of this.player.videoTracks ?? []) {
      const listener = (qualityEvent: QualityEvent<'activequalitychanged'>) => {
        this.handleQualityChanged(qualityEvent, 'video');
      };
      track.addEventListener('activequalitychanged', listener);
      this.videoTrackListeners.set(track, listener);
    }

    this.player.audioTracks?.addEventListener('addtrack', (event) => {
      // The issue is that the track from the addtrack event is typed as the base Track interface,
      // which only has TrackEventMap — not MediaTrackEventMap. We need to narrow the type to MediaTrack
      const mediaTrack = event.track as MediaTrack;

      const listener = (qualityEvent: QualityEvent<'activequalitychanged'>) => {
        this.handleQualityChanged(qualityEvent, 'audio');
      };
      mediaTrack.addEventListener('activequalitychanged', listener);
      this.audioTrackListeners.set(mediaTrack, listener);
    });

    this.player.audioTracks?.addEventListener('removetrack', (event) => {
      // The issue is that the track from the removetrack event is typed as the base Track interface,
      // which only has TrackEventMap — not MediaTrackEventMap. We need to narrow the type to MediaTrack
      const mediaTrack = event.track as MediaTrack;

      const listener = this.audioTrackListeners.get(mediaTrack);
      if (listener) {
        mediaTrack.removeEventListener('activequalitychanged', listener);
        this.audioTrackListeners.delete(mediaTrack);
      }
    });

    // add listeners to all already added/existing audio tracks
    for (const track of this.player.audioTracks ?? []) {
      const listener = (qualityEvent: QualityEvent<'activequalitychanged'>) => {
        this.handleQualityChanged(qualityEvent, 'audio');
      };
      track.addEventListener('activequalitychanged', listener);
      this.audioTrackListeners.set(track, listener);
    }

    this.player.addEventListener('seeking', (_event) => {
      // we have to send the last tracked time as currentTime here,
      // because at this point the event.currentTime is already set to the new seeked time
      this.eventCallback(Event.SEEK, {currentTime: this.lastTrackedCurrentTime});
    });

    this.player.addEventListener('timeupdate', (event) => {
      this.lastTrackedCurrentTime = event.currentTime;

      // For unknown reasons/in unknown cases THEOPlayer seems to sometimes not send  "playing" event after start
      // attempt, leading to elevated VideoStartFailures as our state machine will be stuck in "startup state".
      // This is a best effort workaround where we check the eventTime and the video currentTime for playback progress
      // when in Startup state, to be able to transition out of Startup state when video playback is detected but player
      // did not send the Playing event.
      // This workaround might lead to a little higher startup times than the playback actually showcased, since we need
      // 2 events to be able to reliably tell if the playback progressed and the normal interval between timeupdate
      // events is 250ms.
      if (this.stateMachine.currentState === 'STARTUP') {
        if (this.playbackProgressDetectionService.hasPlaybackProgressed(event.date.getTime(), event.currentTime)) {
          logger.log(
            `[PlaybackProgressDetectionService] Playback progress detected based on "timeupdate" event, eventTime: ${event.date.getTime()}, player currentTime: ${event.currentTime}`,
          );
          this.eventCallback(Event.PLAYING, {currentTime: event.currentTime});
        }
      }
    });

    this.player.addEventListener('playing', (event) => {
      this.eventCallback(Event.PLAYING, {currentTime: event.currentTime});
    });

    this.player.addEventListener('waiting', (event) => {
      this.eventCallback(Event.START_BUFFERING, {currentTime: event.currentTime});
    });

    this.player.addEventListener('ended', (event) => {
      this.eventCallback(Event.END, {currentTime: event.currentTime});
    });

    this.player.addEventListener('error', (event) => {
      const code = event.errorObject.code;
      const message = event.errorObject.message;
      const originalError = event.errorObject.cause;
      const errorAsData = tryJSONStringify(event.errorObject);

      this.eventCallback(Event.ERROR, {
        code: code,
        message: message,
        legacyData: errorAsData,
        currentTime: this.currentTime,
        originalError: originalError,
        data: {
          exceptionMessage: message,
          exceptionStacktrace: event.errorObject.stack?.split('\n') ?? undefined,
          additionalData: errorAsData,
        },
      });

      this.ssaiService.sendAdEngagementErrorSample({code: code, message: message, severity: 'CRITICAL'}, event);
    });

    this.player.ads?.addEventListener('adbreakbegin', (event) => {
      // SSAI ad breaks are tracked via the SsaiService using regular playback samples, so they
      // must not enter the CSAI ad playback state of the state machine.
      if (isSsaiIntegration(event.adBreak.integration)) {
        return;
      }
      this.eventCallback(Event.START_AD, {currentTime: this.currentTime});
    });
    this.player.ads?.addEventListener('adbreakend', (event) => {
      if (isSsaiIntegration(event.adBreak.integration)) {
        return;
      }
      this.eventCallback(Event.END_AD, {currentTime: this.currentTime});
    });
  }

  private registerUnloadEventListeners() {
    /** handler has to be defined as an arrow function to capture the correct "this" */
    const unloadHandler = () => {
      if (this.hasHandledUnload) {
        return;
      }

      this.hasHandledUnload = true;
      this.eventCallback(Event.UNLOAD, {
        // we use here the last tracked video time as we cannot be sure if the player is still working at this place
        currentTime: this.lastTrackedCurrentTime,
      });
      this._adModule.unload();
      this.ssaiService.unload();
      this.release();
    };

    this.player.addEventListener('destroy', unloadHandler);
    this.windowEventTracker.addEventListener('beforeunload', unloadHandler);
    this.windowEventTracker.addEventListener('unload', unloadHandler);
  }
}

export function detectPlaybackInfoStreamTypeFieldsFromSource(
  source: TypedSource | undefined,
): Pick<PlaybackInfo, 'm3u8Url' | 'mpdUrl' | 'progUrl' | 'streamFormat'> {
  if (isNullish(source)) {
    return {};
  }

  let streamType: StreamTypes | undefined = undefined;
  if (!isNullish(source.type)) {
    streamType = getStreamTypeFromMIMEType(source.type);
  }
  if (isNullish(streamType)) {
    streamType = getStreamTypesFromURL(source.src);
  }

  return buildPlaybackInfoStreamTypeFields(streamType, source.src);
}

export function createSpeedMeasurementFromEvent(event: OnDownloadFinishedEventObject): SpeedMeasurement | undefined {
  const httpRequest = event.httpRequest;

  if (!httpRequest.success) {
    return undefined;
  }

  if (httpRequest.type !== HttpRequestType.MEDIA_VIDEO && httpRequest.type !== HttpRequestType.MEDIA_AUDIO) {
    return undefined;
  }

  return {
    duration: httpRequest.downloadTime / 1000,
    size: isNumber(httpRequest.size) ? httpRequest.size : 0,
    timeToFirstByte: isNotNullish(httpRequest.timeToFirstByte) ? httpRequest.timeToFirstByte / 1000 : 0,
    timestamp: new Date(),
    httpStatus: httpRequest.httpStatus,
  };
}
